import { FormEvent, useState } from 'react'
import { formatBRL, formatInt, parseBRNumber, parseDateBR, todayBRString } from './calculators'
import { calculateNegotiationFromConfig, loadOperationalConfigForDate, type DynamicNegotiationResult } from '../../lib/operationalConfig'

function Status({ kind = 'info', children }: { kind?: 'info' | 'ok' | 'warn' | 'error'; children: React.ReactNode }) {
  return <div className={`tool-status ${kind}`} role="status">{children}</div>
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return <article className="tool-metric"><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>
}

function toIso(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

export function DynamicNegotiationTool() {
  const [valor, setValor] = useState('')
  const [dataDivida, setDataDivida] = useState('')
  const [dataRef, setDataRef] = useState(todayBRString())
  const [result, setResult] = useState<DynamicNegotiationResult | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setResult(null)
    const debtDate = parseDateBR(dataDivida)
    const referenceDate = parseDateBR(dataRef)
    if (!debtDate || !referenceDate) return setError('Informe datas válidas no formato dd/mm/aaaa.')

    setLoading(true)
    try {
      const loaded = await loadOperationalConfigForDate('negociacao', toIso(referenceDate))
      setResult(calculateNegotiationFromConfig(loaded.config, loaded.source, parseBRNumber(valor), debtDate, referenceDate))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível calcular a negociação.')
    } finally {
      setLoading(false)
    }
  }

  function clear() { setValor(''); setDataDivida(''); setDataRef(todayBRString()); setResult(null); setError('') }

  return (
    <section className="operational-tool">
      <div className="tool-heading"><div><p className="eyebrow">FERRAMENTA OPERACIONAL</p><h2>Sistema de Negociação de Dívidas</h2><p className="muted">A faixa de desconto é escolhida pela regra publicada vigente na data usada para o cálculo.</p></div><span className="status-chip">Configuração por vigência</span></div>
      <div className="tool-layout">
        <form className="tool-card tool-form compact" onSubmit={(event) => void submit(event)}>
          <label>Valor da dívida (R$)<input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="1.000,00" /></label>
          <label>Data da dívida (dd/mm/aaaa)<input value={dataDivida} onChange={(e) => setDataDivida(e.target.value)} placeholder="01/01/2026" /></label>
          <label>Data para cálculo (dd/mm/aaaa)<input value={dataRef} onChange={(e) => setDataRef(e.target.value)} /></label>
          <div className="tool-rule"><span>Critério</span><strong>Regra anual vigente + quantidade de dias em atraso</strong></div>
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={clear}>Limpar</button><button className="primary-button compact" type="submit" disabled={loading}>{loading ? 'Calculando...' : 'Calcular'}</button></div>
        </form>

        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && !error && <Status>Informe os dados e calcule.</Status>}
          {error && <Status kind="error">{error}</Status>}
          {result && (
            <>
              <Status kind={result.source === 'cache' ? 'warn' : 'ok'}>{result.source === 'cache' ? 'Cálculo realizado com a última regra salva em cache.' : 'Proposta calculada com a regra publicada vigente.'}</Status>
              <div className="result-copy"><strong>{result.configTitle}</strong> · vigência {new Date(`${result.effectiveFrom}T00:00:00`).toLocaleDateString('pt-BR')}</div>
              <div className="tool-metrics three"><Metric label="Dívida original" value={formatBRL(parseBRNumber(valor))} /><Metric label="Dias em atraso" value={formatInt(result.days)} /><Metric label="Faixa aplicada" value={result.bandLabel} /></div>
              <div className="proposal-grid"><article><h4>🏷️ À vista</h4><ul><li>Desconto: <strong>{result.cashDiscount}%</strong></li><li>Paga: <strong>{formatBRL(result.cashValue)}</strong></li><li>Economiza: <strong>{formatBRL(result.cashSavings)}</strong></li></ul></article><article><h4>💳 Parcelado</h4><ul><li>Desconto: <strong>{result.installmentDiscount}%</strong></li><li>Paga: <strong>{formatBRL(result.installmentValue)}</strong></li><li>Economiza: <strong>{formatBRL(result.installmentSavings)}</strong></li><li>Limite: <strong>até {result.maxInstallments}x</strong></li></ul></article></div>
              <div className="result-copy">À vista: pagar {formatBRL(result.cashValue)} com economia de {formatBRL(result.cashSavings)} ({result.cashDiscount}% de desconto). Parcelado: pagar {formatBRL(result.installmentValue)} com economia de {formatBRL(result.installmentSavings)} ({result.installmentDiscount}% de desconto), em até {result.maxInstallments}x.</div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}
